/* eslint-disable @typescript-eslint/no-explicit-any */
import * as signalR from '@microsoft/signalr';
import { Injectable, inject } from '@angular/core';
import { NGXLogger } from 'ngx-logger';
import { BehaviorSubject, Subject } from 'rxjs';
import { IWeather } from 'src/app/modules/weather/weather.module';
import { AuthService } from 'src/app/modules/auth/services/auth.service';
import { environment } from 'src/environments/environment';
import { ISignalRBookingEvent } from '../models/signalr-booking.event';
import { SmsDirection } from 'src/app/modules/sms/models/sms-direction.enum';
import { SmsStatus } from 'src/app/modules/sms/models/sms-status.enum';

const HUB_AVMIN_URL = '/hubs/avmin';

export interface IObjectAction {
  id: number | string;
  action: string;
  data?: any[];
}

@Injectable({ providedIn: 'root' })
export class SignalRService {
  private readonly _authService = inject(AuthService);
  private readonly _connected$ = new BehaviorSubject<boolean>(false);
  private readonly _logger = inject(NGXLogger);

  private _hubConnection!: signalR.HubConnection;

  public readonly bookings$ = new Subject<ISignalRBookingEvent>();
  public readonly connected$ = this._connected$.asObservable();
  public readonly courseIntake$ = new Subject<IObjectAction>();
  public readonly courseIntakeSession$ = new Subject<IObjectAction>();
  public readonly events$ = new Subject<IObjectAction>();
  public readonly item$ = new Subject<IObjectAction>();
  public readonly notifications$ = new Subject<IObjectAction>();
  public readonly opportunity$ = new Subject<IObjectAction>();
  public readonly pilotItemPackages$ = new Subject<IObjectAction>();
  public readonly pipeline$ = new Subject<IObjectAction>();
  public readonly sales$ = new Subject<IObjectAction>();
  public readonly salesRegister$ = new Subject<IObjectAction>();
  public readonly sms$ = new Subject<{
    action: string;
    id: number;
    pilotId: number;
    direction: SmsDirection;
    message: string;
    number: string;
    status: SmsStatus;
  }>();
  public readonly user$ = new Subject<IObjectAction>();
  public readonly weather$ = new Subject<IWeather>();
  public readonly workItem$ = new Subject<IObjectAction>();

  constructor() {
    this._configureHubConnection();
    this._bindAuthService();
  }

  private _bindAuthService() {
    this._authService.currentUser$.subscribe((user) => {
      if (user) {
        this._connectAsync();
      } else {
        this._disconnectAsync();
      }
    });
  }

  private _bindBookings() {
    this._hubConnection.on('booking', (action: string, bookingId: number, data: any) => {
      const bookingData = data[0] as any;

      const e = {
        bookingId,
        action,
        startDateTime: new Date(bookingData.startDateTime as unknown as string),
        endDateTime: new Date(bookingData.endDateTime as unknown as string),
      };

      this.bookings$.next(e);
    });
  }

  private _bindCourses() {
    this._hubConnection.on('course:intake', (action: string, id: number) => {
      this.courseIntake$.next({ id, action });
    });

    this._hubConnection.on('course:intake:session', (action: string, id: number) => {
      this.courseIntakeSession$.next({ id, action });
    });
  }

  private _bindEvents() {
    this._hubConnection.on('event', (action: string, eventId: number) => {
      this.events$.next({ id: eventId, action });
    });
  }

  private _bindItems() {
    this._hubConnection.on('item', (action: string, itemId: number) => {
      this.item$.next({ id: itemId, action });
    });
  }

  private _bindNotifications() {
    this._hubConnection.on('notification', (action: string, notificationId: string) => {
      this.notifications$.next({ id: notificationId, action });
    });
  }

  private _bindOpportunities() {
    this._hubConnection.on('opportunity', (action: string, pilotId: number) => {
      this.opportunity$.next({ id: pilotId, action });
    });
  }

  private _bindPilotItemPackages() {
    this._hubConnection.on('pilotItemPackage', (action: string, pilotId: number) => {
      this.pilotItemPackages$.next({ id: pilotId, action });
    });
  }

  private _bindPipelines() {
    this._hubConnection.on('pipeline', (action: string, pilotId: number) => {
      this.pipeline$.next({ id: pilotId, action });
    });
  }

  private _bindSales() {
    this._hubConnection.on('sale', (action: string, saleId: number, data: any) => {
      this.sales$.next({ id: saleId, action, data });
    });
  }

  private _bindSalesRegisters() {
    this._hubConnection.on('salesRegister', (action: string, registerId: number) => {
      this.salesRegister$.next({ id: registerId, action });
    });
  }

  private _bindSms() {
    this._hubConnection.on('sms', (action: string, smsId: number, data: any) => {
      this.sms$.next({
        action,
        id: smsId,
        pilotId: data[0].pilotId,
        direction: data[0].direction,
        message: data[0].message,
        number: data[0].number,
        status: data[0].status,
      });
    });
  }

  private _bindUsers() {
    this._hubConnection.on('user', (action: string, pilotId: number) => {
      this.user$.next({ id: pilotId, action });

      if (pilotId !== this._authService.currentUser?.id) {
        return;
      }

      switch (action) {
        case 'locked':
          this._logger.debug('[SignalR][user] locked');
          this._authService.signOut().subscribe();
          break;

        case 'roles:updated':
          this._logger.debug('[SignalR][user] roles:updated');
          this._authService.refreshToken().subscribe();
          break;
      }
    });
  }

  private _bindWeather() {
    this._hubConnection.on('met', (action: string, id: number, data: any) => {
      switch (action) {
        case 'data':
          this.weather$.next(data[0]);
          break;
      }
    });
  }

  private _bindWorkItems() {
    this._hubConnection.on('workItem', (action: string, workItemId: number, data: any) => {
      this.workItem$.next({ id: workItemId, action, data });
    });
  }

  private _configureHubConnection() {
    this._hubConnection = new signalR.HubConnectionBuilder()
      .configureLogging(environment.production ? signalR.LogLevel.Information : signalR.LogLevel.Debug)
      .withAutomaticReconnect()
      .withUrl(HUB_AVMIN_URL, {
        accessTokenFactory: () => this._authService.getAuthToken(),
      })
      .build();

    this._hubConnection.onclose(() => {
      this._hubClosedAsync();
    });

    this._bindBookings();
    this._bindCourses();
    this._bindEvents();
    this._bindItems();
    this._bindNotifications();
    this._bindOpportunities();
    this._bindPilotItemPackages();
    this._bindPipelines();
    this._bindSales();
    this._bindSalesRegisters();
    this._bindSms();
    this._bindUsers();
    this._bindWeather();
    this._bindWorkItems();
  }

  private async _connectAsync() {
    if (this._hubConnection.state !== signalR.HubConnectionState.Disconnected) {
      await this._disconnectAsync();
      return;
    }

    this._logger.debug('[SignalR][connect]');
    this._hubConnection
      .start()
      .then(() => {
        this._logger.debug('[SignalR][connect] connected');
        this._connected$.next(true);
      })
      .catch((error) => {
        this._logger.error('[SignalR][connect]', error);
        this._connected$.next(false);
      });
  }

  private async _disconnectAsync(): Promise<void> {
    await this._hubConnection?.stop();
  }

  private async _hubClosedAsync(): Promise<void> {
    this._logger.debug('[SignalR][hubClosed]');

    if (this._authService.currentUser) {
      this._logger.debug('[SignalR][hubClosed] reconnecting');
      this._connected$.next(false);
      this._connectAsync();
    }
  }
}
